/**
 * EPIC 54 P2 — the invitation a client user arrives holding.
 *
 * Both of these have to be callables. Since P1 the rules judge a person by
 * their membership status, so somebody who has been invited and not accepted
 * cannot read `clientMemberships` and cannot read the company document
 * either — they would not learn the name of the firm inviting them. Correct
 * for a direct read; useless for the screen that must ask "do you accept?".
 */

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';

export type InvitationOutcome =
  | 'prepared' | 'sent' | 'send_failed' | 'accepted' | 'expired' | 'cancelled';

/** An employer company, or this person's own B2C record. */
export type ClientKind = 'company' | 'individual';

export interface MyMembership {
  clientId: string;
  companyName: string;
  /**
   * Which of the two this is. Without it the portal merged an employer
   * company and a personal B2C record into one candidate list and called
   * the result a conflict.
   */
  clientType: ClientKind;
  status: 'prepared' | 'invited' | 'active' | 'suspended' | 'removed';
  companyRole: 'member' | 'admin';
  invitation?: {
    outcome: InvitationOutcome;
    sentAt?: string;
    expiresAt?: string;
    error?: string;
    /** UX 6.4 (Q26) — set when the invitee said "Ei ole minu kutse". */
    declinedAt?: string;
  };
  invitedAt?: string;
  /** UX 6.4 — who invited them (name only). */
  invitedByName?: string;
  acceptedAt?: string;
  preparedSites: {
    siteId: string; name: string; role: string;
    permissions?: string[];
    startsAt?: string;
    endsAt?: string;
  }[];
  acceptable: boolean;
}

/** A client reached only through the legacy `users.permissions` list. */
export interface MyLegacyLink {
  clientId: string;
  companyName: string;
  clientType: ClientKind;
}

export interface MyMembershipsResult {
  memberships: MyMembership[];
  /** Typed by the server, because a client cannot read a company it is not in. */
  legacyLinks?: MyLegacyLink[];
  /**
   * Every company the new model holds a membership document for, WHATEVER
   * its status — suspended, withdrawn and unaccepted ones included.
   *
   * Absent from an older server, in which case the memberships it did
   * return are the best available answer. It is what lets the portal tell a
   * deliberately inactive relation from a company it has never heard of:
   * the first must not be revived by a stale `users.permissions` entry, and
   * the second is exactly what that list is still for.
   */
  knownClientIds?: string[];
  hasActive: boolean;
  /** An EMPLOYER company — a personal B2C record does not count. */
  hasActiveCompany?: boolean;
  pending: number;
}

export async function listMyClientInvitations(): Promise<MyMembershipsResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<Record<string, never>, MyMembershipsResult>(
    functions,
    'listMyClientInvitations',
  );
  const { data } = await callable({});
  return data;
}

export async function acceptClientInvitation(clientId: string): Promise<void> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ clientId: string }, { activated: boolean }>(
    functions,
    'acceptClientInvitation',
  );
  await callable({ clientId });
}

/**
 * UX 6.4 (Q26) — "Ei ole minu kutse": the invitee withdraws an invitation
 * that reached the wrong person. The company's administrators are told.
 */
export async function declineClientInvitation(
  clientId: string,
): Promise<{ declined: boolean; repeated: boolean }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ clientId: string }, { declined: boolean; repeated: boolean }>(
    functions,
    'declineClientInvitation',
  );
  const { data } = await callable({ clientId });
  return data;
}
